"use client";

import React, { useEffect, useState } from "react";
import POSReceivingAssets from "./POSReceivingAssets";
import CreatePaymentFlow from "./CreatePaymentFlow";
import DPOSPaymentHistory from "./DPOSPaymentHistory";
import DPOSPerformanceChart from "./DPOSPerformanceChart";
import CashierModeFlow from "./CashierModeFlow";
import {
  useActiveBusiness,
  useDposCashierModeStatus,
} from "../../hooks/query/dposQueries";
import { useQueryClient } from "@tanstack/react-query";
import {
  clearCreatePaymentFlowState,
  readCreatePaymentFlowState,
} from "./dposFlowPersistence";

type View = "overview" | "create-payment" | "cashier-mode";

export default function DPOSPage() {
  const { businessId, isLoading } = useActiveBusiness();
  const [view, setView] = useState<View>("overview");
  const [refreshSignal, setRefreshSignal] = useState(0);
  const queryClient = useQueryClient();
  const cashierModeStatus = useDposCashierModeStatus(businessId);

  useEffect(() => {
    if (readCreatePaymentFlowState()) {
      setView("create-payment");
    }
  }, []);

  useEffect(() => {
    if (cashierModeStatus.data?.session?.status === "active") {
      setView("cashier-mode");
      return;
    }

    setView((currentView) =>
      currentView === "cashier-mode" ? "overview" : currentView
    );
  }, [cashierModeStatus.data?.session?.status]);

  const goToOverview = () => {
    clearCreatePaymentFlowState();
    queryClient.invalidateQueries({ queryKey: ["dposPerformance"] });
    setRefreshSignal((s) => s + 1);
    setView("overview");
  };

  if (view === "create-payment") {
    return (
      <CreatePaymentFlow onClose={goToOverview} onComplete={goToOverview} />
    );
  }

  if (view === "cashier-mode") {
    return <CashierModeFlow businessId={businessId} onExit={goToOverview} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap lg:flex-nowrap gap-6">
        {/* Left: performance chart — 3/5 */}
        <div className="w-full lg:w-3/5 flex flex-col">
          <DPOSPerformanceChart
            businessId={businessId || ""}
            isPageLoading={isLoading}
          />
        </div>

        {/* Right: action buttons + POS assets card — 2/5 */}
        <div className="w-full lg:w-2/5 flex flex-col gap-4">
          {/* Buttons outside the white card — full width, equal split */}
          <div className="flex gap-3 w-full">
            <button
              onClick={() => setView("cashier-mode")}
              className="flex-1 border border-[#EBE9EF] bg-white rounded-full h-[48px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors"
            >
              <span className="text-[14px] font-semibold text-[#6D6584]">
                Cashier Mode
              </span>
            </button>
            <button
              onClick={() => setView("create-payment")}
              className="flex-1 rounded-full h-[48px] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
              style={{
                backgroundImage:
                  "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
              }}
            >
              <span className="text-[14px] font-semibold">Create Payment</span>
            </button>
          </div>

          {/* White card: POS receiving assets */}
          <div className="flex-1">
            <POSReceivingAssets
              businessId={businessId || ""}
              isPageLoading={isLoading}
              onCreatePayment={() => setView("create-payment")}
              onCashierMode={() => setView("cashier-mode")}
            />
          </div>
        </div>
      </div>
      <DPOSPaymentHistory
        businessId={businessId || ""}
        isPageLoading={isLoading}
        refreshSignal={refreshSignal}
      />
    </div>
  );
}
